import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../../shared/Button';
import styles from './Hero.module.css';

const AUTOPLAY_MS = 5000;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A scroll-snap carousel rather than a scripted slider: the track is a real
 * scrolling element, so a trackpad, a touch swipe and the arrow keys all work
 * without any of it being reimplemented. The dots and arrows only scroll it.
 */
const HeroCarousel = ({ media }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((next) => {
    const track = trackRef.current;
    if (!track) return;
    const target = ((next % media.length) + media.length) % media.length;
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  }, [media.length]);

  // Follow the track rather than drive it, so a manual swipe keeps the dots
  // honest.
  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    setIndex(Math.min(Math.max(current, 0), media.length - 1));
  }, [media.length]);

  useEffect(() => {
    if (paused || media.length < 2 || prefersReducedMotion()) return undefined;
    const timer = window.setInterval(() => {
      const track = trackRef.current;
      if (!track || track.clientWidth === 0) return;
      const current = Math.round(track.scrollLeft / track.clientWidth);
      goTo(current + 1);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused, media.length, goTo]);

  const hold = () => setPaused(true);
  const release = () => setPaused(false);

  return (
    <div
      className={styles.heroCarousel}
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocusCapture={hold}
      onBlurCapture={release}
    >
      <div
        className={styles.heroTrack}
        ref={trackRef}
        onScroll={onScroll}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label="Product dashboards"
      >
        {media.map((item, i) => (
          <figure
            key={item.src}
            className={styles.heroSlide}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${media.length}: ${item.label || 'Dashboard'}`}
          >
            <img src={item.src} alt={item.alt} loading={i === 0 ? 'eager' : 'lazy'} />
          </figure>
        ))}
      </div>

      {media.length > 1 && (
        <>
          <button
            type="button"
            className={`${styles.heroArrow} ${styles.heroArrowPrev}`}
            onClick={() => goTo(index - 1)}
            aria-label="Previous dashboard"
          >
            <ChevronLeft size={18} strokeWidth={2.4} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`${styles.heroArrow} ${styles.heroArrowNext}`}
            onClick={() => goTo(index + 1)}
            aria-label="Next dashboard"
          >
            <ChevronRight size={18} strokeWidth={2.4} aria-hidden="true" />
          </button>

          <div className={styles.heroDots}>
            {media.map((item, i) => (
              <button
                key={item.src}
                type="button"
                className={i === index ? `${styles.heroDot} ${styles.heroDotOn}` : styles.heroDot}
                onClick={() => goTo(i)}
                aria-label={item.label || `Dashboard ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
              >
                <span>{item.label || i + 1}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Hero = ({
  title,
  description,
  ctaText,
  onCtaClick,
  variant = 'default',
  className = '',
  media = null
}) => {
  const heroClass = [styles.hero, styles[variant], className].filter(Boolean).join(' ');
  const hasMedia = Array.isArray(media) && media.length > 0;
  const innerClass = [styles.heroInner, hasMedia ? styles.heroInnerSplit : '']
    .filter(Boolean)
    .join(' ');

  return (
    <section className={heroClass}>
      <div className={innerClass}>
        <div className={styles.heroContent}>
          <h1>{title}</h1>
          <p>{description}</p>
          {ctaText && (
            <Button
              variant="primary"
              onClick={onCtaClick}
              className={styles.ctaButton}
            >
              {ctaText}
            </Button>
          )}
        </div>

        {hasMedia && <HeroCarousel media={media} />}
      </div>
    </section>
  );
};

export default Hero;
